import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { api } from "./api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";

// ── Brand tokens ───────────────────────────────────────────────────────────────
const C = {
  green:      "#147B2C",
  greenHover: "#1a9936",
  greenDim:   "#0d5c20",
  greenTint:  "#F0FDF4",
  navy:       "#001B30",
  navyMid:    "#002A4A",
  navyLight:  "#0A3A5C",
  white:      "#FFFFFF",
  bg:         "#F4F7FA",
  border:     "#E2E8F0",
  text:       "#0F172A",
  textMuted:  "#475569",
  textFaint:  "#94A3B8",
  amber:      "#F59E0B",
  amberTint:  "#FFFBEB",
  red:        "#EF4444",
  redTint:    "#FEF2F2",
  teal:       "#0D9488",
  tealTint:   "#F0FDFA",
  slate:      "#F8FAFC",
};

// ── Static data ────────────────────────────────────────────────────────────────
const DEPT_CODES  = ["EXO","ADM","OPS","PDT","FIN","HR","BD","SLS","MNB","LGL"];
const DOC_TYPES   = ["SA","NDA","EA","POL","INV","SOP","MOU","PRJ","PRP","QUT","CON","LOI","LOE","PA","CA"];
const STATUSES    = ["Draft","Active","Superseded","Archived"];
const VERSIONS    = ["V1","V2","V3","V4","V5"];
const RECORD_TYPES = ["Base","Amendment","Version Update"];
const YEARS       = ["2025","2026","2027","2028","2029","2030"];

const EMPTY_FORM = {
  dept:"", docType:"", year:"", version:"", subVersion:"",
  title:"", counterParty:"", issueDate:"", effectiveDate:"", expiryDate:"",
  status:"Draft", recordType:"Base", parentDRN:"", amendNo:"", versionBumpType:"minor",
};

const STATUS_STYLE = {
  Active:     { bg: "#DCFCE7", text: "#15803D", dot: "#22C55E" },
  Draft:      { bg: "#FEF9C3", text: "#A16207", dot: "#EAB308" },
  Superseded: { bg: "#F1F5F9", text: "#475569", dot: "#94A3B8" },
  Archived:   { bg: "#FEE2E2", text: "#B91C1C", dot: "#EF4444" },
};

const PIE_COLORS = ["#22C55E", "#EAB308", "#94A3B8", "#EF4444"];

// ── Helpers ────────────────────────────────────────────────────────────────────
function docId(r) { return r._id || r.id; }

function formatDate(d) {
  if (!d) return "";
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function autoNextVersion(version, bumpType = "minor") {
  const match = (version || "").match(/^V(\d+)(?:\.(\d+))?$/);
  if (!match) return bumpType === "major" ? "V2" : "V1.1";
  const major = parseInt(match[1]);
  const minor = match[2] !== undefined ? parseInt(match[2]) : null;
  if (bumpType === "major") return `V${major + 1}`;
  return minor !== null ? `V${major}.${minor + 1}` : `V${major}.1`;
}

function generateDRN(form, records) {
  if (!form.dept || !form.docType || !form.year || !form.version) return "";
  if (form.recordType === "Amendment") {
    return form.parentDRN ? `${form.parentDRN}-AMD${String(form.amendNo || 1).padStart(2, "0")}` : "";
  }
  if (form.recordType === "Version Update") {
    if (!form.parentDRN) return "";
    return form.parentDRN.replace(/-V\d+(\.\d+)?$/, `-${form.version}`);
  }
  const seq = records.filter(r =>
    r.dept === form.dept && r.docType === form.docType &&
    r.year === form.year && r.recordType === "Base"
  ).length + 1;
  const ver = form.subVersion ? `${form.version}.${form.subVersion}` : form.version;
  return `EFF-${form.dept}-${form.docType}-${form.year}-${String(seq).padStart(4, "0")}-${ver}`;
}

function exportCSV(records) {
  const headers = ["DRN","Title","Dept","Doc Type","Year","Version","Counter Party","Issue Date","Expiry Date","Status","Record Type","Parent DRN"];
  const rows = records.map(r => [
    r.drn, r.title, r.dept, r.docType, r.year, r.version,
    r.counterParty || "", formatDate(r.issueDate), formatDate(r.expiryDate), r.status, r.recordType, r.parentDRN || "",
  ]);
  const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `MDCR_Export_${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ── Micro components ───────────────────────────────────────────────────────────
const Icon = ({ name, size = 16, color = "currentColor" }) => {
  const paths = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/></>,
    register:  <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>,
    plus:      <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    search:    <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    trash:     <><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></>,
    x:         <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
    check:     <><polyline points="20 6 9 17 4 12"/></>,
    alert:     <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    doc:       <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
    menu:      <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>,
    download:  <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    refresh:   <><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>,
    layers:    <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
    link:      <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
    info:      <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>,
    edit:      <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {paths[name]}
    </svg>
  );
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Superseded;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 13, fontWeight: 700,
      background: s.bg, color: s.text, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const Btn = ({ variant = "primary", onClick, disabled, children, style: extra = {} }) => {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "9px 18px", borderRadius: 8, fontSize: 15, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", border: "none",
    transition: "all 0.15s", opacity: disabled ? 0.55 : 1,
    fontFamily: "inherit",
  };
  const variants = {
    primary:   { background: C.green,    color: C.white },
    secondary: { background: C.navy,     color: C.white },
    ghost:     { background: "transparent", color: C.textMuted, border: `1.5px solid ${C.border}` },
    danger:    { background: C.red,      color: C.white },
    teal:      { background: C.teal,     color: C.white },
  };
  return (
    <button style={{ ...base, ...variants[variant], ...extra }} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

const FormGroup = ({ label, required, children, span = 1 }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: span === 2 ? "1 / -1" : undefined }}>
    <label style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em", textTransform: "uppercase" }}>
      {label}{required && <span style={{ color: C.red, marginLeft: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`,
  fontSize: 15, color: C.text, outline: "none", background: C.white,
  fontFamily: "inherit", width: "100%", transition: "border-color 0.15s",
};

// ── Custom tooltip for charts ──────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.navy, color: C.white, padding: "8px 14px", borderRadius: 8, fontSize: 14, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.25)" }}>
      <span style={{ color: C.textFaint, marginRight: 8 }}>{label}</span>
      <span style={{ color: "#4ADE80" }}>{payload[0]?.value}</span>
    </div>
  );
};

// ── Skeleton loader ────────────────────────────────────────────────────────────
const Skeleton = ({ width = "100%", height = 16, radius = 6 }) => (
  <div style={{ width, height, borderRadius: radius, background: `linear-gradient(90deg, ${C.border} 25%, #f1f5f9 50%, ${C.border} 75%)`, backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }} />
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════════
export default function MDCRApp() {
  const [view,        setView]        = useState("dashboard");
  const [records,     setRecords]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [search,      setSearch]      = useState("");
  const [filterDept,  setFilterDept]  = useState("");
  const [filterStatus,setFilterStatus]= useState("");
  const [filterType,  setFilterType]  = useState("");
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [notification,  setNotification]  = useState(null);
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [editingStatus, setEditingStatus] = useState(null); // docId being edited
  const [updatingStatus,setUpdatingStatus]= useState(null); // docId being saved
  const [helpOpen,      setHelpOpen]      = useState(false);
  const [editRecord,    setEditRecord]    = useState(null); // { record, fields }
  const [saving,        setSaving]        = useState(false);
  const [authed,        setAuthed]        = useState(() => sessionStorage.getItem("mdcr_auth") === "1");
  const [loginForm,     setLoginForm]     = useState({ user: "", pass: "", error: "" });
  const notifTimer = useRef(null);

  // ── Data loading ─────────────────────────────────────────────────────────────
  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await api.getDocuments();
      setRecords(Array.isArray(docs) ? docs : []);
    } catch (e) {
      console.error("Failed to load documents", e);
      notify("Failed to load documents from database.", "error");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // ── Notifications ─────────────────────────────────────────────────────────────
  const notify = (msg, type = "success") => {
    if (notifTimer.current) clearTimeout(notifTimer.current);
    setNotification({ msg, type });
    notifTimer.current = setTimeout(() => setNotification(null), 4000);
  };

  // ── Derived data ──────────────────────────────────────────────────────────────
  const generatedDRN = useMemo(() => generateDRN(form, records), [form, records]);

  const filtered = useMemo(() => records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.drn || "").toLowerCase().includes(q) ||
      (r.title || "").toLowerCase().includes(q) ||
      (r.counterParty || "").toLowerCase().includes(q) ||
      (r.dept || "").toLowerCase().includes(q);
    const matchDept   = !filterDept   || r.dept      === filterDept;
    const matchStatus = !filterStatus || r.status    === filterStatus;
    const matchType   = !filterType   || r.recordType === filterType;
    return matchSearch && matchDept && matchStatus && matchType;
  }), [records, search, filterDept, filterStatus, filterType]);

  const stats = useMemo(() => ({
    total:      records.length,
    active:     records.filter(r => r.status === "Active").length,
    draft:      records.filter(r => r.status === "Draft").length,
    amendments: records.filter(r => r.recordType === "Amendment" || r.recordType === "Version Update").length,
  }), [records]);

  const deptChartData = useMemo(() =>
    DEPT_CODES
      .map(d => ({ name: d, count: records.filter(r => r.dept === d && r.status === "Active").length }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count),
  [records]);

  const docTypeChartData = useMemo(() =>
    DOC_TYPES
      .map(d => ({ name: d, count: records.filter(r => r.docType === d && r.status === "Active").length }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count),
  [records]);

  const statusPieData = useMemo(() =>
    STATUSES
      .map(s => ({ name: s, value: records.filter(r => r.status === s).length }))
      .filter(d => d.value > 0),
  [records]);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleStatusChange = async (record, newStatus) => {
    if (newStatus === record.status) { setEditingStatus(null); return; }
    const id = String(docId(record));
    setUpdatingStatus(id);
    setEditingStatus(null);
    try {
      const result = await api.updateDocument(id, { status: newStatus });
      if (result?.error) { notify(result.error, "error"); return; }
      setRecords(rs => rs.map(r => String(docId(r)) === id ? { ...r, status: newStatus } : r));
      notify(`Status updated to ${newStatus}`);
    } catch (e) {
      notify("Failed to update status.", "error");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleParentDRNChange = (drn, recordType) => {
    const rt = recordType || form.recordType;
    const parent = records.find(r => r.drn === drn);
    if (parent) {
      if (rt === "Amendment") {
        const existingAmends = records.filter(r => r.parentDRN === drn && r.recordType === "Amendment").length;
        setForm(f => ({ ...f, parentDRN: drn, dept: parent.dept, docType: parent.docType, year: parent.year, version: parent.version, title: parent.title, counterParty: parent.counterParty || "", amendNo: existingAmends + 1 }));
      } else if (rt === "Version Update") {
        const nextVer = autoNextVersion(parent.version, form.versionBumpType);
        setForm(f => ({ ...f, parentDRN: drn, dept: parent.dept, docType: parent.docType, year: parent.year, version: nextVer, title: parent.title, counterParty: parent.counterParty || "" }));
      }
    } else {
      setForm(f => ({ ...f, parentDRN: drn }));
    }
  };

  const handleBumpTypeChange = (bumpType) => {
    if (!form.parentDRN) { setForm(f => ({ ...f, versionBumpType: bumpType })); return; }
    const parent = records.find(r => r.drn === form.parentDRN);
    const nextVer = parent ? autoNextVersion(parent.version, bumpType) : form.version;
    setForm(f => ({ ...f, versionBumpType: bumpType, version: nextVer }));
  };

  const handleSubmit = async () => {
    if (!form.dept || !form.docType || !form.year || !form.version || !form.title || !form.status || !form.recordType) {
      notify("Please fill all required fields.", "error"); return;
    }
    if (!generatedDRN) { notify("Cannot generate DRN — check all fields.", "error"); return; }

    setSubmitting(true);
    try {
      const payload = {
        drn:          generatedDRN,
        title:        form.title,
        dept:         form.dept,
        docType:      form.docType,
        year:         form.year,
        version:      form.version,
        counterParty: form.counterParty || "",
        issueDate:    form.issueDate || "",
        effectiveDate:form.effectiveDate || "",
        expiryDate:   form.expiryDate || "",
        status:       form.status,
        recordType:   form.recordType,
        parentDRN:    form.parentDRN || "",
        amendRef:     form.recordType === "Amendment" ? generatedDRN : "",
      };

      const result = await api.createDocument(payload);

      if (result?.error) {
        if (result.error === "Duplicate DRN")     notify(`DRN already exists: ${result.drn}`, "error");
        else if (result.error === "Similar record exists") notify(`Similar record already exists: ${result.similarDRN}`, "error");
        else notify(result.error, "error");
        return;
      }

      // Version Update: auto-supersede the parent document
      if (form.recordType === "Version Update" && form.parentDRN) {
        const parent = records.find(r => r.drn === form.parentDRN);
        if (parent) {
          const parentId = String(docId(parent));
          await api.updateDocument(parentId, { status: "Superseded" });
          setRecords(rs => rs.map(r => String(docId(r)) === parentId ? { ...r, status: "Superseded" } : r));
        }
      }

      setRecords(r => [result, ...r]);
      setSubmitSuccess(generatedDRN);
      setForm(EMPTY_FORM);
      notify(`Registered: ${generatedDRN}`);
      setTimeout(() => setSubmitSuccess(null), 5000);
    } catch (e) {
      console.error(e);
      notify("Failed to save document — check your connection.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = async () => {
    if (!editRecord) return;
    setSaving(true);
    try {
      const id = String(docId(editRecord.record));
      const result = await api.updateDocument(id, editRecord.fields);
      if (result?.error) { notify(result.error, "error"); return; }
      setRecords(rs => rs.map(r => String(docId(r)) === id ? { ...r, ...editRecord.fields } : r));
      notify("Record updated successfully.");
      setEditRecord(null);
    } catch (e) {
      notify("Failed to save — check your connection.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    const hasChildren = records.some(r => r.parentDRN === record.drn);
    setDeleteConfirm({ record, hasChildren });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const id = String(docId(deleteConfirm.record));
    setDeleting(true);
    try {
      const result = await api.deleteDocument(id);
      if (result?.error) {
        notify(result.error === "Has amendments"
          ? `Cannot delete — this document has ${result.count} amendment(s) linked to it.`
          : result.error, "error");
        setDeleteConfirm(null);
        return;
      }
      setRecords(r => r.filter(x => String(docId(x)) !== id));
      notify(`Deleted: ${deleteConfirm.record.drn}`);
      setDeleteConfirm(null);
    } catch (e) {
      notify("Failed to delete document.", "error");
    } finally {
      setDeleting(false);
    }
  };

  // ── Auth ──────────────────────────────────────────────────────────────────
  const AUTH_USER = "effecon";
  const AUTH_PASS = "Mdcr@2026";

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.user.trim() === AUTH_USER && loginForm.pass === AUTH_PASS) {
      sessionStorage.setItem("mdcr_auth", "1");
      setAuthed(true);
    } else {
      setLoginForm(f => ({ ...f, error: "Incorrect username or password." }));
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("mdcr_auth");
    setAuthed(false);
    setLoginForm({ user: "", pass: "", error: "" });
  };

  // ── Login screen ──────────────────────────────────────────────────────────
  if (!authed) return (
    <div style={{ fontFamily: "'Roboto', 'Segoe UI', sans-serif", minHeight: "100vh", background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 400 }}>
        {/* Logo + brand */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <img src="/images/Logo_ColorPallette.png" alt="Effecon" style={{ height: 52, objectFit: "contain", marginBottom: 14 }} onError={e => { e.target.style.display = "none"; }} />
          <div style={{ fontSize: 24, fontWeight: 700, color: C.white, letterSpacing: "-0.01em" }}>Effecon MDCR</div>
          <div style={{ fontSize: 15, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>Master Document Control Register</div>
        </div>

        {/* Card */}
        <div style={{ background: C.white, borderRadius: 16, padding: "32px 32px 28px", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 6 }}>Sign in</div>
          <div style={{ fontSize: 15, color: C.textMuted, marginBottom: 24 }}>Internal access only · Effecon BV</div>

          <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em", textTransform: "uppercase" }}>Username</label>
              <input
                type="text" autoComplete="username" autoFocus
                value={loginForm.user}
                onChange={e => setLoginForm(f => ({ ...f, user: e.target.value, error: "" }))}
                style={{ ...inputStyle }}
                placeholder="effecon"
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em", textTransform: "uppercase" }}>Password</label>
              <input
                type="password" autoComplete="current-password"
                value={loginForm.pass}
                onChange={e => setLoginForm(f => ({ ...f, pass: e.target.value, error: "" }))}
                style={{ ...inputStyle }}
                placeholder="••••••••"
              />
            </div>
            {loginForm.error && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: C.redTint, borderRadius: 8, border: `1px solid #FECACA` }}>
                <Icon name="alert" size={14} color={C.red} />
                <span style={{ fontSize: 14.5, color: C.red, fontWeight: 500 }}>{loginForm.error}</span>
              </div>
            )}
            <button type="submit" style={{
              marginTop: 4, padding: "11px", borderRadius: 8, background: C.green, color: C.white,
              border: "none", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.greenHover}
            onMouseLeave={e => e.currentTarget.style.background = C.green}>
              Sign in
            </button>
          </form>
        </div>

        <div style={{ textAlign: "center", marginTop: 20, fontSize: 14, color: "rgba(255,255,255,0.2)" }}>
          © 2026 Effecon BV — Internal use only
        </div>
      </div>
    </div>
  );

  // ── Sidebar nav items ──────────────────────────────────────────────────────
  const navItems = [
    { id: "dashboard", label: "Dashboard",     icon: "dashboard" },
    { id: "register",  label: "Master Register", icon: "register" },
    { id: "generator", label: "DRN Generator",  icon: "plus" },
  ];

  const navigate = (id) => { setView(id); setSidebarOpen(false); };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: "'Roboto', 'Segoe UI', sans-serif", background: C.bg, minHeight: "100vh", color: C.text }}>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap" rel="stylesheet" />

      {/* ── SIDEBAR ─────────────────────────────────────────────────────────── */}
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,27,48,0.55)", zIndex: 199, display: "none" }}
          className="mobile-overlay"
        />
      )}

      <aside className={`sidebar${sidebarOpen ? " sidebar-open" : ""}`} style={{
        width: 240, minWidth: 240, background: C.navy,
        position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 200,
        display: "flex", flexDirection: "column", overflowY: "auto",
      }}>
        {/* Logo area */}
        <div style={{ padding: "28px 20px 22px", borderBottom: `1px solid rgba(255,255,255,0.07)` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <img
              src="/images/Logo_ColorPallette.png"
              alt="Effecon logo"
              style={{ height: 36, objectFit: "contain", display: "block" }}
              onError={e => { e.target.style.display = "none"; }}
            />
            <span style={{ fontSize: 17, fontWeight: 800, color: C.green, letterSpacing: "-0.01em", lineHeight: 1 }}>
              Effecon BV
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.green }} />
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Master Document Control
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ padding: "16px 12px", flex: 1 }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "0 8px", marginBottom: 8 }}>
            Navigation
          </div>
          {navItems.map(item => {
            const active = view === item.id;
            return (
              <button key={item.id} onClick={() => navigate(item.id)} style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%", padding: "10px 12px", borderRadius: 8,
                cursor: "pointer", border: "none", textAlign: "left",
                background: active ? "rgba(20,123,44,0.18)" : "transparent",
                color: active ? "#4ADE80" : "rgba(255,255,255,0.55)",
                fontSize: 15.5, fontWeight: active ? 700 : 500,
                transition: "all 0.15s", marginBottom: 2,
                borderLeft: active ? `3px solid ${C.green}` : "3px solid transparent",
                fontFamily: "inherit",
              }}>
                <Icon name={item.icon} size={15} color={active ? "#4ADE80" : "rgba(255,255,255,0.45)"} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Stats chip */}
        <div style={{ margin: "0 12px 20px", padding: "14px 16px", background: "rgba(255,255,255,0.05)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>
            At a Glance
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "Total",     value: stats.total,      color: C.white },
              { label: "Active",    value: stats.active,     color: "#4ADE80" },
              { label: "Draft",     value: stats.draft,      color: "#FCD34D" },
              { label: "Amendments",value: stats.amendments, color: "#67E8F9" },
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", fontWeight: 600, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Logout */}
        <button onClick={handleLogout} style={{
          margin: "0 12px 16px", display: "flex", alignItems: "center", gap: 8,
          padding: "9px 12px", borderRadius: 8, background: "none",
          border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer",
          color: "rgba(255,255,255,0.35)", fontSize: 14, fontWeight: 500,
          fontFamily: "inherit", transition: "all 0.15s", width: "calc(100% - 24px)",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "rgba(255,255,255,0.35)"; }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>
      </aside>

      {/* ── MOBILE TOPBAR ───────────────────────────────────────────────────── */}
      <div className="mobile-topbar" style={{
        display: "none", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", background: C.navy, position: "fixed", top: 0, left: 0, right: 0, zIndex: 150,
        borderBottom: `1px solid rgba(255,255,255,0.08)`,
      }}>
        <button onClick={() => setSidebarOpen(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
          <Icon name="menu" size={22} color={C.white} />
        </button>
        <span style={{ fontSize: 15, fontWeight: 800, color: C.white, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Effecon MDCR
        </span>
        <div style={{ width: 30 }} />
      </div>

      {/* ── MAIN CONTENT ────────────────────────────────────────────────────── */}
      <main className="main-content" style={{ marginLeft: 240, padding: "36px 40px", minHeight: "100vh" }}>

        {/* ══ DASHBOARD ══════════════════════════════════════════════════════ */}
        {view === "dashboard" && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 12 }}>
              <div>
                <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: 0, letterSpacing: "-0.02em" }}>Dashboard</h1>
                <p style={{ fontSize: 15.5, color: C.textMuted, marginTop: 4, margin: 0 }}>
                  Live overview of all controlled documents · {new Date().toLocaleDateString("en-BE", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
              <Btn variant="ghost" onClick={loadDocuments}>
                <Icon name="refresh" size={13} /> Refresh
              </Btn>
            </div>

            {/* Stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 28 }} className="stats-grid">
              {[
                { label: "Total Documents", value: stats.total,      accent: C.navy,  icon: "doc",      tint: "#EEF2FF" },
                { label: "Active",          value: stats.active,     accent: C.green, icon: "check",    tint: C.greenTint },
                { label: "Draft",           value: stats.draft,      accent: C.amber, icon: "layers",   tint: C.amberTint },
                { label: "Amendments",      value: stats.amendments, accent: C.teal,  icon: "link",     tint: C.tealTint },
              ].map(card => (
                <div key={card.label} style={{
                  background: C.white, borderRadius: 14, padding: "22px 24px",
                  border: `1px solid ${C.border}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                  transition: "box-shadow 0.2s",
                }} className="stat-card">
                  <div>
                    <div style={{ fontSize: 38, fontWeight: 800, color: C.text, lineHeight: 1.1 }}>
                      {loading ? <Skeleton width={48} height={36} /> : card.value}
                    </div>
                    <div style={{ fontSize: 14, color: C.textMuted, marginTop: 6, fontWeight: 600, letterSpacing: "0.04em" }}>
                      {card.label}
                    </div>
                  </div>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: card.tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name={card.icon} size={18} color={card.accent} />
                  </div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }} className="charts-grid">
              {/* Active by Department */}
              <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Active by Department</div>
                    <div style={{ fontSize: 14, color: C.textMuted, marginTop: 2 }}>Distribution of active docs</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green }} />
                </div>
                <div style={{ padding: "20px 16px 16px" }}>
                  {deptChartData.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "32px 0", color: C.textFaint, fontSize: 15 }}>No active documents</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(deptChartData.length * 36, 120)}>
                      <BarChart data={deptChartData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="name" width={40} tick={{ fontSize: 13, fill: C.textMuted, fontWeight: 600, fontFamily: "Roboto, sans-serif" }} axisLine={false} tickLine={false} />
                        <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(20,123,44,0.06)" }} />
                        <Bar dataKey="count" fill={C.green} radius={[0, 5, 5, 0]} maxBarSize={20}>
                          {deptChartData.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? C.green : `rgba(20,123,44,${0.8 - i * 0.06})`} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Active by Document Type */}
              <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Active by Document Type</div>
                    <div style={{ fontSize: 14, color: C.textMuted, marginTop: 2 }}>Document type breakdown</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.navy }} />
                </div>
                <div style={{ padding: "20px 16px 16px" }}>
                  {docTypeChartData.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "32px 0", color: C.textFaint, fontSize: 15 }}>No active documents</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(docTypeChartData.length * 36, 120)}>
                      <BarChart data={docTypeChartData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="name" width={40} tick={{ fontSize: 13, fill: C.textMuted, fontWeight: 600, fontFamily: "Roboto, sans-serif" }} axisLine={false} tickLine={false} />
                        <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,27,48,0.05)" }} />
                        <Bar dataKey="count" fill={C.navy} radius={[0, 5, 5, 0]} maxBarSize={20}>
                          {docTypeChartData.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? C.navy : `rgba(0,27,48,${0.85 - i * 0.05})`} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            {/* Status donut */}
            <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", padding: "18px 24px" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Status Distribution</div>
              <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 16 }}>All records by lifecycle status</div>
              {statusPieData.length === 0 ? (
                <div style={{ textAlign: "center", padding: "24px 0", color: C.textFaint, fontSize: 15 }}>No data yet</div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 24 }}>
                  <ResponsiveContainer width={200} height={160}>
                    <PieChart>
                      <Pie data={statusPieData} cx={95} cy={75} innerRadius={45} outerRadius={70}
                        dataKey="value" paddingAngle={3} startAngle={90} endAngle={450}>
                        {statusPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.15)", fontSize: 14 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {statusPieData.map((d, i) => (
                      <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                        <span style={{ fontSize: 15, fontWeight: 600, color: C.text, minWidth: 90 }}>{d.name}</span>
                        <span style={{ fontSize: 15, color: C.textMuted }}>{d.value} doc{d.value !== 1 ? "s" : ""}</span>
                        <span style={{ fontSize: 13, color: C.textFaint }}>({records.length ? Math.round(d.value / records.length * 100) : 0}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ MASTER REGISTER ════════════════════════════════════════════════ */}
        {view === "register" && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
              <div>
                <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: 0, letterSpacing: "-0.02em" }}>Master Register</h1>
                <p style={{ fontSize: 15.5, color: C.textMuted, marginTop: 4, margin: 0 }}>
                  {loading ? "Loading…" : `${filtered.length} of ${records.length} records`}
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Btn variant="ghost" onClick={() => exportCSV(filtered)}>
                  <Icon name="download" size={13} /> Export CSV
                </Btn>
                <Btn variant="primary" onClick={() => setView("generator")}>
                  <Icon name="plus" size={14} color="white" /> New Document
                </Btn>
              </div>
            </div>

            <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
              {/* Search + filters */}
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", background: C.slate }}>
                <div style={{ position: "relative", flex: "1 1 220px" }}>
                  <div style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                    <Icon name="search" size={14} color={C.textFaint} />
                  </div>
                  <input
                    placeholder="Search DRN, title, counterparty…"
                    value={search} onChange={e => setSearch(e.target.value)}
                    style={{ ...inputStyle, paddingLeft: 34, background: C.white }}
                  />
                </div>
                {[
                  { val: filterDept,   set: setFilterDept,   opts: DEPT_CODES,   label: "All Departments" },
                  { val: filterStatus, set: setFilterStatus, opts: STATUSES,     label: "All Statuses" },
                  { val: filterType,   set: setFilterType,   opts: RECORD_TYPES, label: "All Types" },
                ].map((f, i) => (
                  <select key={i} value={f.val} onChange={e => f.set(e.target.value)}
                    style={{ ...inputStyle, width: "auto", minWidth: 130, cursor: "pointer", paddingRight: 28 }}>
                    <option value="">{f.label}</option>
                    {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ))}
                {(search || filterDept || filterStatus || filterType) && (
                  <Btn variant="ghost" onClick={() => { setSearch(""); setFilterDept(""); setFilterStatus(""); setFilterType(""); }}
                    style={{ padding: "9px 12px" }}>
                    <Icon name="x" size={13} /> Clear
                  </Btn>
                )}
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: C.slate }}>
                      {["DRN","Title","Dept","Type","Year","Counter Party","Issue Date","Status","Record",""].map(h => (
                        <th key={h} style={{
                          padding: "10px 16px", textAlign: "left", fontSize: 13, fontWeight: 700,
                          color: C.textMuted, letterSpacing: "0.07em", textTransform: "uppercase",
                          borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 10 }).map((_, j) => (
                            <td key={j} style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
                              <Skeleton height={12} width={j === 0 ? 160 : j === 1 ? 140 : 50} />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: "52px 24px", textAlign: "center" }}>
                          <Icon name="doc" size={32} color={C.border} />
                          <div style={{ fontSize: 16, color: C.textMuted, marginTop: 12, fontWeight: 600 }}>No records found</div>
                          <div style={{ fontSize: 15, color: C.textFaint, marginTop: 4 }}>
                            {records.length === 0 ? "Add your first document using the DRN Generator." : "Try adjusting your search or filters."}
                          </div>
                        </td>
                      </tr>
                    ) : filtered.map((r, i) => (
                      <tr key={String(docId(r))} className="table-row" style={{ background: i % 2 === 0 ? C.white : C.slate, transition: "background 0.1s" }}>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" }}>
                          <span style={{ fontFamily: "monospace", fontSize: 13.5, fontWeight: 700, color: C.green, letterSpacing: "0.02em" }}>{r.drn}</span>
                        </td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, maxWidth: 240, verticalAlign: "middle" }}>
                          <div style={{ fontWeight: 600, fontSize: 15, color: C.text, lineHeight: 1.3 }}>{r.title}</div>
                          {r.parentDRN && <div style={{ fontSize: 12.5, color: C.textFaint, marginTop: 3 }}>↳ {r.parentDRN}</div>}
                        </td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.text, background: C.border, padding: "2px 7px", borderRadius: 5 }}>{r.dept}</span>
                        </td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.teal }}>{r.docType}</span>
                        </td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 15, color: C.textMuted, verticalAlign: "middle" }}>{r.year}</td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 14.5, color: C.textMuted, maxWidth: 160, verticalAlign: "middle" }}>{r.counterParty || <span style={{ color: C.textFaint }}>—</span>}</td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 14, color: C.textMuted, whiteSpace: "nowrap", verticalAlign: "middle" }}>
                          <div>{formatDate(r.issueDate) || <span style={{ color: C.textFaint }}>—</span>}</div>
                          {r.expiryDate && <div style={{ fontSize: 12.5, color: C.amber, marginTop: 2, fontWeight: 600 }}>Exp: {formatDate(r.expiryDate)}</div>}
                        </td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" }}>
                          <div style={{ position: "relative", display: "inline-block" }}>
                            {updatingStatus === String(docId(r)) ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 13, fontWeight: 700, background: C.border, color: C.textMuted }}>
                                <span style={{ width: 10, height: 10, border: `2px solid ${C.textFaint}`, borderTopColor: C.textMuted, borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                                Saving…
                              </span>
                            ) : (
                              <button
                                onClick={() => setEditingStatus(editingStatus === String(docId(r)) ? null : String(docId(r)))}
                                title="Click to change status"
                                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "block" }}
                              >
                                <StatusBadge status={r.status} />
                              </button>
                            )}
                            {editingStatus === String(docId(r)) && (
                              <>
                                <div onClick={() => setEditingStatus(null)} style={{ position: "fixed", inset: 0, zIndex: 299 }} />
                                <div style={{
                                  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 300,
                                  background: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
                                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden", minWidth: 150,
                                }}>
                                  <div style={{ padding: "8px 12px 6px", fontSize: 12, fontWeight: 700, color: C.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>
                                    Change status
                                  </div>
                                  {STATUSES.map(s => (
                                    <button key={s} onClick={() => handleStatusChange(r, s)} style={{
                                      display: "flex", alignItems: "center", gap: 8, width: "100%",
                                      padding: "9px 12px", background: s === r.status ? C.slate : "none",
                                      border: "none", cursor: "pointer", fontFamily: "inherit",
                                      fontSize: 15, fontWeight: s === r.status ? 700 : 500, color: C.text,
                                      transition: "background 0.1s",
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = C.slate}
                                    onMouseLeave={e => e.currentTarget.style.background = s === r.status ? C.slate : "none"}
                                    >
                                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_STYLE[s]?.dot, flexShrink: 0 }} />
                                      {s}
                                      {s === r.status && <span style={{ marginLeft: "auto", color: C.green, fontSize: 13 }}>✓</span>}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" }}>
                          <span style={{
                            fontSize: 12.5, fontWeight: 700,
                            color: r.recordType === "Amendment" ? C.teal : r.recordType === "Version Update" ? "#7C3AED" : C.textMuted,
                            background: r.recordType === "Amendment" ? C.tealTint : r.recordType === "Version Update" ? "#F5F3FF" : C.slate,
                            padding: "2px 8px", borderRadius: 5, whiteSpace: "nowrap",
                          }}>{r.recordType}</span>
                        </td>
                        <td style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              onClick={() => setEditRecord({ record: r, fields: { title: r.title, counterParty: r.counterParty || "", issueDate: r.issueDate || "", effectiveDate: r.effectiveDate || "", expiryDate: r.expiryDate || "", status: r.status } })}
                              title="Edit record"
                              style={{ background: "none", border: `1.5px solid ${C.border}`, borderRadius: 7, padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center", transition: "all 0.15s" }}
                            >
                              <Icon name="edit" size={13} color={C.textMuted} />
                            </button>
                            <button
                              onClick={() => handleDelete(r)}
                              title="Delete record"
                              style={{ background: "none", border: `1.5px solid ${C.border}`, borderRadius: 7, padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center", transition: "all 0.15s" }}
                              className="delete-btn"
                            >
                              <Icon name="trash" size={13} color={C.red} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              {!loading && filtered.length > 0 && (
                <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, fontSize: 14, color: C.textFaint, background: C.slate, display: "flex", justifyContent: "space-between" }}>
                  <span>Showing {filtered.length} of {records.length} records</span>
                  <span>{stats.active} active · {stats.draft} draft · {stats.amendments} amendments</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ DRN GENERATOR ══════════════════════════════════════════════════ */}
        {view === "generator" && (
          <div style={{ animation: "fadeUp 0.3s ease", maxWidth: 840 }}>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: 0, letterSpacing: "-0.02em" }}>DRN Generator</h1>
              <p style={{ fontSize: 15.5, color: C.textMuted, marginTop: 4, margin: 0 }}>Register a new controlled document into the MDCR</p>
            </div>

            {/* ── Collapsible Help & Guide ── */}
            <div style={{ marginBottom: 20, background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
              <button
                onClick={() => setHelpOpen(v => !v)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "14px 18px", background: "none", border: "none", cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="info" size={14} color="#3B82F6" />
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>How to use the DRN Generator</span>
                  <span style={{ fontSize: 13, color: C.textFaint, fontWeight: 500 }}>— click to {helpOpen ? "hide" : "show"}</span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.textFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: helpOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {helpOpen && (
                <div style={{ padding: "0 18px 18px", borderTop: `1px solid ${C.border}` }}>
                  {/* Quick Steps */}
                  <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Quick Start</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[
                        ["1", "Select Record Type", "Choose Base, Amendment, or Version Update (see guide below)."],
                        ["2", "Fill Core Identifiers", "Pick Department, Document Type, Year. Version auto-fills for Amendments and Version Updates."],
                        ["3", "Add Document Details", "Enter title, counter party (for contracts), issue date, and optionally an expiry date."],
                        ["4", "Review DRN Preview", "The reference number at the bottom generates automatically. Check it before submitting."],
                        ["5", "Submit to Register", "Click Submit — the record is saved to MongoDB and appears in the Master Register immediately."],
                      ].map(([num, title, desc]) => (
                        <div key={num} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                          <span style={{ width: 20, height: 20, borderRadius: 6, background: C.navy, color: C.white, fontSize: 12, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{num}</span>
                          <div>
                            <span style={{ fontSize: 14.5, fontWeight: 700, color: C.text }}>{title} — </span>
                            <span style={{ fontSize: 14.5, color: C.textMuted }}>{desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ height: 1, background: C.border, margin: "4px 0 16px" }} />

                  {/* Record Type Guide */}
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Record Type Guide</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }} className="help-grid">
                    {[
                      {
                        type: "Base",
                        color: C.green, tint: C.greenTint,
                        when: "First time creating a document — no prior version exists.",
                        example: "New NDA, a fresh SOP, first policy document.",
                        result: "Parent stays Active. New record gets a fresh sequence number (0001, 0002…).",
                        badge: "EFF-LGL-NDA-2026-0001-V1",
                      },
                      {
                        type: "Amendment",
                        color: C.teal, tint: C.tealTint,
                        when: "Adding a supplementary clause or addendum to an existing live document — both remain in force simultaneously.",
                        example: "Adding a new clause to a signed NDA or contract.",
                        result: "Parent stays Active. Amendment is also Active. Both are operative together.",
                        badge: "EFF-LGL-NDA-2026-0001-V1-AMD01",
                      },
                      {
                        type: "Version Update",
                        color: "#7C3AED", tint: "#F5F3FF",
                        when: "Replacing a document with a newer version — old version is superseded. Choose Minor (V1→V1.1) for small corrections or Major (V1→V2) for significant rewrites.",
                        example: "Minor: fixing wording in an SOP (V1→V1.1). Major: full overhaul of a policy (V1→V2).",
                        result: "Parent is automatically marked Superseded on submit. New version becomes Active.",
                        badge: "Minor: V1→V1.1 · Major: V1→V2",
                      },
                    ].map(item => (
                      <div key={item.type} style={{ background: item.tint, borderRadius: 10, padding: "14px", border: `1px solid ${item.color}22` }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: item.color, marginBottom: 8 }}>{item.type}</div>
                        <div style={{ fontSize: 13.5, color: C.text, marginBottom: 6, lineHeight: 1.5 }}><strong>When:</strong> {item.when}</div>
                        <div style={{ fontSize: 13.5, color: C.textMuted, marginBottom: 6, lineHeight: 1.5 }}><strong>Example:</strong> {item.example}</div>
                        <div style={{ fontSize: 13.5, color: C.textMuted, marginBottom: 8, lineHeight: 1.5 }}><strong>Result:</strong> {item.result}</div>
                        <div style={{ fontFamily: "monospace", fontSize: 12, color: item.color, background: "rgba(255,255,255,0.6)", padding: "4px 8px", borderRadius: 5, wordBreak: "break-all" }}>{item.badge}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 14, padding: "10px 14px", background: C.amberTint, borderRadius: 8, border: `1px solid #FDE68A` }}>
                    <span style={{ fontSize: 14, color: "#92400E", fontWeight: 600 }}>Tip: </span>
                    <span style={{ fontSize: 14, color: "#92400E" }}>Use <strong>Clear Form</strong> at any time to reset all fields. The system will block duplicate DRNs and warn you if a similar Base record already exists.</span>
                  </div>
                </div>
              )}
            </div>

            {/* Success banner */}
            {submitSuccess && (
              <div style={{ background: C.greenTint, border: `1px solid #86EFAC`, borderRadius: 12, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: C.green, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="check" size={16} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.greenDim }}>Document registered successfully</div>
                  <div style={{ fontSize: 14, color: "#166534", marginTop: 2, fontFamily: "monospace" }}>{submitSuccess}</div>
                </div>
              </div>
            )}

            <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
              {/* Section: Core identifiers */}
              <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, background: C.slate }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 20, height: 20, borderRadius: 6, background: C.navy, color: C.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800 }}>1</span>
                  Core Identifiers
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, padding: "24px 24px 20px" }} className="form-grid">
                <FormGroup label="Record Type" required>
                  <select value={form.recordType} onChange={e => {
                    const rt = e.target.value;
                    setForm(f => ({ ...EMPTY_FORM, recordType: rt, dept: rt === "Base" ? f.dept : "", docType: rt === "Base" ? f.docType : "", year: rt === "Base" ? f.year : "" }));
                  }} style={inputStyle}>
                    {RECORD_TYPES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </FormGroup>
                <FormGroup label="Department" required>
                  <select value={form.dept} onChange={e => setForm(f => ({ ...f, dept: e.target.value }))} style={inputStyle}>
                    <option value="">— Select —</option>
                    {DEPT_CODES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </FormGroup>
                <FormGroup label="Document Type" required>
                  <select value={form.docType} onChange={e => setForm(f => ({ ...f, docType: e.target.value }))} style={inputStyle}>
                    <option value="">— Select —</option>
                    {DOC_TYPES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </FormGroup>
                <FormGroup label="Year" required>
                  <select value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} style={inputStyle}>
                    <option value="">— Select —</option>
                    {YEARS.map(o => <option key={o}>{o}</option>)}
                  </select>
                </FormGroup>
                <FormGroup label="Version" required>
                  <select value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} style={inputStyle}>
                    <option value="">— Select —</option>
                    {VERSIONS.map(o => <option key={o}>{o}</option>)}
                  </select>
                </FormGroup>
                <FormGroup label="Sub-Version" >
                  <input value={form.subVersion} onChange={e => setForm(f => ({ ...f, subVersion: e.target.value }))} placeholder="e.g. 1 → yields V1.1" style={inputStyle} />
                </FormGroup>
              </div>

              {/* Amendment parent selector */}
              {form.recordType === "Amendment" && (
                <div style={{ margin: "0 24px 20px", padding: "16px 18px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1D4ED8", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>
                    Amendment Details
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" }}>
                    <FormGroup label="Parent DRN" required>
                      <select value={form.parentDRN} onChange={e => handleParentDRNChange(e.target.value, "Amendment")} style={inputStyle}>
                        <option value="">— Select parent document —</option>
                        {records.filter(r => r.recordType === "Base" || r.recordType === "Version Update").map(r => (
                          <option key={String(docId(r))} value={r.drn}>{r.drn} — {r.title}</option>
                        ))}
                      </select>
                    </FormGroup>
                    <FormGroup label="AMD No. (auto)">
                      <input value={form.amendNo ? `AMD${String(form.amendNo).padStart(2,"0")}` : "—"} readOnly
                        style={{ ...inputStyle, background: C.slate, color: C.textMuted, width: 100, textAlign: "center", fontFamily: "monospace", fontWeight: 700 }} />
                    </FormGroup>
                  </div>
                  {form.parentDRN && (
                    <div style={{ marginTop: 10, fontSize: 14, color: "#1D4ED8" }}>
                      Auto-filled from parent · {records.filter(r => r.parentDRN === form.parentDRN && r.recordType === "Amendment").length} existing amendment(s)
                    </div>
                  )}
                </div>
              )}

              {/* Version Update parent selector */}
              {form.recordType === "Version Update" && (
                <div style={{ margin: "0 24px 20px", padding: "16px 18px", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>
                    Version Update Details
                  </div>

                  {/* Major / Minor toggle */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    {[
                      { val: "minor", label: "Minor update", desc: "Small change · V1→V1.1" },
                      { val: "major", label: "Major update", desc: "Significant rewrite · V1→V2" },
                    ].map(opt => {
                      const active = form.versionBumpType === opt.val;
                      return (
                        <button key={opt.val} onClick={() => handleBumpTypeChange(opt.val)} style={{
                          flex: 1, padding: "10px 12px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                          border: active ? "2px solid #7C3AED" : `2px solid #DDD6FE`,
                          background: active ? "#EDE9FE" : C.white, fontFamily: "inherit", transition: "all 0.15s",
                        }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: active ? "#7C3AED" : C.textMuted }}>{opt.label}</div>
                          <div style={{ fontSize: 13, color: active ? "#6D28D9" : C.textFaint, marginTop: 2 }}>{opt.desc}</div>
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" }}>
                    <FormGroup label="Previous Version DRN" required>
                      <select value={form.parentDRN} onChange={e => handleParentDRNChange(e.target.value, "Version Update")} style={inputStyle}>
                        <option value="">— Select document to supersede —</option>
                        {records.filter(r => r.recordType === "Base" || r.recordType === "Version Update").map(r => (
                          <option key={String(docId(r))} value={r.drn}>{r.drn} — {r.title}</option>
                        ))}
                      </select>
                    </FormGroup>
                    <FormGroup label="New Version (auto)">
                      <input value={form.version || "—"} readOnly
                        style={{ ...inputStyle, background: C.slate, color: "#7C3AED", width: 100, textAlign: "center", fontFamily: "monospace", fontWeight: 700 }} />
                    </FormGroup>
                  </div>

                  {form.parentDRN && (
                    <div style={{ marginTop: 10, fontSize: 14, color: "#7C3AED" }}>
                      {form.versionBumpType === "minor" ? "Minor update" : "Major update"} · {records.find(r => r.drn === form.parentDRN)?.version} → <strong>{form.version}</strong> · previous record will be marked <strong>Superseded</strong> on submit
                    </div>
                  )}
                </div>
              )}

              {/* Section: Document details */}
              <div style={{ padding: "20px 24px", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.slate }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 20, height: 20, borderRadius: 6, background: C.navy, color: C.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800 }}>2</span>
                  Document Details
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, padding: "24px 24px 20px" }} className="form-grid">
                <FormGroup label="Document Title" required span={2}>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Enter the full document title" style={inputStyle} />
                </FormGroup>
                <FormGroup label="Counter Party">
                  <input value={form.counterParty} onChange={e => setForm(f => ({ ...f, counterParty: e.target.value }))} placeholder="Name of external party (if any)" style={inputStyle} />
                </FormGroup>
                <FormGroup label="Status" required>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                    {STATUSES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </FormGroup>
                <FormGroup label="Issue Date">
                  <input type="date" value={form.issueDate} onChange={e => setForm(f => ({ ...f, issueDate: e.target.value }))} style={inputStyle} />
                </FormGroup>
                <FormGroup label="Effective Date">
                  <input type="date" value={form.effectiveDate} onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))} style={inputStyle} />
                </FormGroup>
                <FormGroup label="Expiry Date">
                  <input type="date" value={form.expiryDate} onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))} style={inputStyle} />
                </FormGroup>
              </div>

              {/* DRN Preview */}
              <div style={{ margin: "0 24px 24px", background: C.navy, borderRadius: 12, padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>
                    {form.recordType === "Amendment" ? "Generated Amendment Reference" : form.recordType === "Version Update" ? "Generated Version Update Reference" : "Generated Document Reference Number"}
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 800, color: generatedDRN ? "#4ADE80" : "rgba(255,255,255,0.2)", letterSpacing: "0.04em", transition: "color 0.2s" }}>
                    {generatedDRN || "EFF-DEPT-TYPE-YEAR-NNNN-VN"}
                  </div>
                </div>
                {generatedDRN ? (
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(74,222,128,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name="check" size={18} color="#4ADE80" />
                  </div>
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name="info" size={18} color="rgba(255,255,255,0.25)" />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, justifyContent: "flex-end", background: C.slate }}>
                <Btn variant="ghost" onClick={() => setForm(EMPTY_FORM)} disabled={submitting}>
                  Clear Form
                </Btn>
                <Btn variant="primary" onClick={handleSubmit} disabled={submitting || !generatedDRN}>
                  {submitting ? (
                    <><span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: C.white, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Saving…</>
                  ) : (
                    <><Icon name="doc" size={14} color="white" /> Submit to Register</>
                  )}
                </Btn>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ── EDIT MODAL ────────────────────────────────────────────────────── */}
      {editRecord && (
        <div onClick={() => !saving && setEditRecord(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,27,48,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, borderRadius: 16, padding: 28, maxWidth: 560, width: "100%",
            boxShadow: "0 24px 64px rgba(0,0,0,0.3)", animation: "fadeUp 0.2s ease",
          }}>
            {/* Header */}
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="edit" size={20} color="#1D4ED8" />
              </div>
              <div>
                <div style={{ fontSize: 19, fontWeight: 800, color: C.text }}>Edit Record</div>
                <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2, fontFamily: "monospace", fontWeight: 700, color: C.green }}>{editRecord.record.drn}</div>
              </div>
            </div>

            {/* Locked fields notice */}
            <div style={{ background: C.slate, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: 13, color: C.textMuted }}>
              <strong>Locked:</strong> DRN · Dept · Type · Year · Version — these cannot be changed as they form the document identity.
            </div>

            {/* Editable fields */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.textMuted, marginBottom: 5 }}>Title</label>
                <input value={editRecord.fields.title} onChange={e => setEditRecord(er => ({ ...er, fields: { ...er.fields, title: e.target.value } }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.textMuted, marginBottom: 5 }}>Counter Party</label>
                <input value={editRecord.fields.counterParty} onChange={e => setEditRecord(er => ({ ...er, fields: { ...er.fields, counterParty: e.target.value } }))}
                  placeholder="External party name" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.textMuted, marginBottom: 5 }}>Status</label>
                <select value={editRecord.fields.status} onChange={e => setEditRecord(er => ({ ...er, fields: { ...er.fields, status: e.target.value } }))}
                  style={inputStyle}>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.textMuted, marginBottom: 5 }}>Issue Date</label>
                <input type="date" value={editRecord.fields.issueDate} onChange={e => setEditRecord(er => ({ ...er, fields: { ...er.fields, issueDate: e.target.value } }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.textMuted, marginBottom: 5 }}>Effective Date</label>
                <input type="date" value={editRecord.fields.effectiveDate} onChange={e => setEditRecord(er => ({ ...er, fields: { ...er.fields, effectiveDate: e.target.value } }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.textMuted, marginBottom: 5 }}>Expiry Date</label>
                <input type="date" value={editRecord.fields.expiryDate} onChange={e => setEditRecord(er => ({ ...er, fields: { ...er.fields, expiryDate: e.target.value } }))}
                  style={inputStyle} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setEditRecord(null)} disabled={saving}>Cancel</Btn>
              <Btn variant="primary" onClick={handleEditSave} disabled={saving}>
                {saving ? "Saving…" : "Save Changes"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ──────────────────────────────────────────────────── */}
      {deleteConfirm && (
        <div
          onClick={() => !deleting && setDeleteConfirm(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,27,48,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, borderRadius: 16, padding: 28, maxWidth: 460, width: "100%",
            boxShadow: "0 24px 64px rgba(0,0,0,0.3)", animation: "fadeUp 0.2s ease",
          }}>
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: C.redTint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="trash" size={20} color={C.red} />
              </div>
              <div>
                <div style={{ fontSize: 19, fontWeight: 800, color: C.text }}>Delete Record</div>
                <div style={{ fontSize: 15, color: C.textMuted, marginTop: 2 }}>This action cannot be undone</div>
              </div>
            </div>

            <div style={{ background: C.slate, borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: `1px solid ${C.border}` }}>
              <div style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 700, color: C.green, marginBottom: 4 }}>{deleteConfirm.record.drn}</div>
              <div style={{ fontSize: 15, color: C.text, fontWeight: 500 }}>{deleteConfirm.record.title}</div>
            </div>

            {deleteConfirm.hasChildren && (
              <div style={{ background: C.amberTint, border: `1px solid #FDE68A`, borderRadius: 10, padding: "12px 14px", marginBottom: 16, display: "flex", gap: 10 }}>
                <Icon name="alert" size={15} color={C.amber} />
                <span style={{ fontSize: 14.5, color: "#92400E", fontWeight: 600, lineHeight: 1.5 }}>
                  This record has amendments linked to it. The API will block deletion to prevent orphaned records.
                </span>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Btn>
              <Btn variant="danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete Record"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── TOAST ─────────────────────────────────────────────────────────── */}
      {notification && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          padding: "13px 20px", borderRadius: 12, fontWeight: 700, fontSize: 15,
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 28px rgba(0,0,0,0.2)",
          background: notification.type === "error" ? C.red : C.green,
          color: C.white, animation: "slideUp 0.25s ease",
          maxWidth: 380,
        }}>
          <Icon name={notification.type === "error" ? "alert" : "check"} size={15} color="white" />
          {notification.msg}
        </div>
      )}

      {/* ── GLOBAL STYLES ─────────────────────────────────────────────────── */}
      <style>{`
        @keyframes fadeUp  { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

        * { box-sizing: border-box; margin: 0; padding: 0; }

        input:focus, select:focus {
          border-color: ${C.green} !important;
          box-shadow: 0 0 0 3px rgba(20,123,44,0.1);
          outline: none;
        }
        .stat-card:hover  { box-shadow: 0 4px 16px rgba(0,0,0,0.08) !important; }
        .table-row:hover  { background: #F0FDF4 !important; }
        .delete-btn:hover { background: ${C.redTint} !important; border-color: ${C.red} !important; }
        button:active     { transform: scale(0.98); }

        ::-webkit-scrollbar       { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 10px; }

        @media (max-width: 900px) {
          .sidebar          { transform: translateX(-100%); transition: transform 0.25s ease; }
          .sidebar-open     { transform: translateX(0) !important; }
          .mobile-overlay   { display: block !important; }
          .mobile-topbar    { display: flex !important; }
          .main-content     { margin-left: 0 !important; padding: 72px 16px 24px !important; }
          .stats-grid       { grid-template-columns: repeat(2,1fr) !important; }
          .charts-grid      { grid-template-columns: 1fr !important; }
          .form-grid        { grid-template-columns: 1fr !important; }
          .help-grid        { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}
